import { useState, useCallback } from "react";
import { Languages, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * TranslateButton — one-click translate a message into the current UI language.
 * State machine:
 *   idle          → shows "Translate" link
 *   translating   → spinner
 *   translated    → shows translation below original + "Show original" toggle
 *
 * Uses `useTranslation().i18n.language` to determine target. Result cached
 * per message id in local component state — server also caches by content
 * hash so repeat views cost nothing.
 */
export default function TranslateButton({ messageId, text, className = "" }) {
  const { i18n, t } = useTranslation();
  const [state, setState] = useState("idle");
  const [translation, setTranslation] = useState(null);

  const targetLang = (i18n.language || "en").slice(0, 2).toLowerCase();

  const translate = useCallback(async () => {
    if (!text) return;
    setState("translating");
    try {
      const { data } = await api.post("/messages/translate", {
        text, target_lang: targetLang,
      });
      setTranslation(data.translation);
      setState("translated");
    } catch (e) {
      toast.error(formatApiError(e));
      setState("idle");
    }
  }, [text, targetLang]);

  if (state === "translated" && translation) {
    return (
      <div className={`mt-1 ${className}`} data-testid={`translate-block-${messageId}`}>
        <div className="text-[10px] uppercase font-black text-brand-accent flex items-center gap-1">
          <Languages size={10} /> {t("chat.translated", "Translated")} · {targetLang.toUpperCase()}
        </div>
        <div className="whitespace-pre-wrap break-words text-sm mt-0.5">{translation}</div>
        <button
          onClick={() => setState("idle")}
          className="mt-1 text-[10px] text-ink-textDim hover:text-white underline"
          data-testid={`translate-hide-${messageId}`}
        >
          {t("chat.showOriginal", "Show original")}
        </button>
      </div>
    );
  }

  if (state === "translating") {
    return (
      <div className={`mt-1 text-[10px] text-ink-textDim inline-flex items-center gap-1 ${className}`}>
        <Loader2 size={10} className="animate-spin" /> {t("chat.translating", "Translating…")}
      </div>
    );
  }

  return (
    <button
      onClick={translate}
      className={`mt-1 text-[10px] text-ink-textDim hover:text-brand-accent inline-flex items-center gap-1 underline underline-offset-2 ${className}`}
      data-testid={`translate-btn-${messageId}`}
    >
      <Languages size={10} /> {t("chat.translate", "Translate")}
    </button>
  );
}
